import { useEffect, useMemo, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { computeTaxes } from './taxes';

const MARGIN = { top: 16, right: 16, bottom: 28, left: 44 };
const CHROME = {
  light: { grid: '#e6e5e1', textSecondary: '#52514e' },
  dark: { grid: '#2c2c2a', textSecondary: '#c3c2b7' },
};

const formatMillio = (eft) =>
  `${(eft / 1000).toLocaleString('hu-HU', { maximumFractionDigits: 1 })} M`;
const formatPct = (v) =>
  `${v.toLocaleString('hu-HU', { maximumFractionDigits: 1 })}%`;

const niceStep = (max) => (max <= 40000 ? 5000 : max <= 100000 ? 10000 : 20000);

// Összes adó a bevétel %-ában, éves bevétel függvényében.
// A költség a jelenlegi költség/bevétel arányban nő a bevétellel.
function TaxChart({ params, expenseRatio, currentIncome, schemes, height }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState(null);
  const theme = useTheme();
  const { grid: GRID, textSecondary: TEXT_SECONDARY } = CHROME[theme.palette.mode];
  const SURFACE = theme.palette.background.paper;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(entry.contentRect.width, 240))
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const xMax = Math.min(
    Math.max(40000, Math.ceil((currentIncome * 1.5) / 10000) * 10000),
    200000
  );
  const xMin = 1000;

  const points = useMemo(() => {
    const n = 160;
    return Array.from({ length: n + 1 }, (_, i) => {
      const income = xMin + ((xMax - xMin) * i) / n;
      const taxes = computeTaxes({
        ...params,
        income,
        expense: income * expenseRatio,
      });
      const values = {};
      schemes.forEach(({ key }) => {
        values[key] =
          key === 'atalany' && !taxes.atalany.available
            ? null
            : (taxes[key].total / income) * 100;
      });
      return { income, values };
    });
  }, [params, expenseRatio, xMax, schemes]);

  // 5 M Ft felett nézzük a skálát, hogy a kis bevételnél kiugró járulék ne nyomja össze a grafikont
  const yMax = Math.min(
    60,
    Math.max(
      20,
      Math.ceil(
        Math.max(
          ...points
            .filter((p) => p.income >= 5000)
            .flatMap((p) => Object.values(p.values).filter((v) => v !== null))
        ) / 10
      ) * 10
    )
  );

  const plotW = width - MARGIN.left - MARGIN.right;
  const plotH = height - MARGIN.top - MARGIN.bottom;
  const x = (v) => MARGIN.left + ((v - xMin) / (xMax - xMin)) * plotW;
  const y = (v) => MARGIN.top + plotH - (v / yMax) * plotH;

  const linePath = (key) => {
    let d = '';
    let pen = false;
    points.forEach((p) => {
      const v = p.values[key];
      if (v === null || !Number.isFinite(v)) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(p.income).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    });
    return d;
  };

  const xStep = niceStep(xMax);
  const xTicks = [];
  for (let t = xStep; t <= xMax; t += xStep) xTicks.push(t);
  const yTicks = [];
  for (let t = 0; t <= yMax; t += 10) yTicks.push(t);

  const indexAt = (clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const px = clientX - rect.left;
    const ratio = (px - MARGIN.left) / plotW;
    return Math.round(Math.min(Math.max(ratio, 0), 1) * (points.length - 1));
  };

  const hovered = hover !== null ? points[hover] : null;
  const showCurrent = currentIncome >= xMin && currentIncome <= xMax;
  const tooltipLeft = hovered ? x(hovered.income) : 0;
  const tooltipOnLeft = tooltipLeft > width / 2;

  return (
    <Box sx={{ marginTop: 3 }}>
      <Typography variant='h6'>Melyik éri meg?</Typography>
      <Typography variant='body2' sx={{ color: TEXT_SECONDARY }}>
        Összes adó a bevétel %-ában, éves bevétel szerint. A költséget a
        bevétel {formatPct(expenseRatio * 100)}-ának vesszük.
      </Typography>
      <Box
        component='ul'
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 16px',
          listStyle: 'none',
          padding: 0,
          margin: '12px 0 4px',
        }}
      >
        {schemes.map((s) => (
          <Box
            component='li'
            key={s.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              sx={{
                width: 14,
                height: 2,
                borderRadius: 1,
                backgroundColor: s.color,
              }}
            />
            <Typography variant='body2'>{s.label}</Typography>
          </Box>
        ))}
      </Box>
      <Box
        ref={containerRef}
        sx={{ position: 'relative', width: '100%', touchAction: 'pan-y' }}
      >
        <svg
          width={width}
          height={height}
          role='img'
          aria-label='Összes adó a bevétel százalékában, adózási formánként'
          tabIndex={0}
          style={{ display: 'block', outline: 'none' }}
          onPointerMove={(e) => setHover(indexAt(e.clientX))}
          onPointerDown={(e) => setHover(indexAt(e.clientX))}
          onPointerLeave={() => setHover(null)}
          onFocus={() =>
            setHover(
              Math.round(
                ((Math.min(Math.max(currentIncome, xMin), xMax) - xMin) /
                  (xMax - xMin)) *
                  (points.length - 1)
              )
            )
          }
          onBlur={() => setHover(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
              e.preventDefault();
              const delta = e.key === 'ArrowRight' ? 1 : -1;
              setHover((h) =>
                Math.min(Math.max((h ?? 0) + delta, 0), points.length - 1)
              );
            }
          }}
        >
          <defs>
            <clipPath id='tax-chart-plot'>
              <rect
                x={MARGIN.left}
                y={MARGIN.top - 2}
                width={plotW}
                height={plotH + 4}
              />
            </clipPath>
          </defs>
          {yTicks.map((t) => (
            <g key={t}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + plotW}
                y1={y(t)}
                y2={y(t)}
                stroke={GRID}
                strokeWidth={1}
              />
              <text
                x={MARGIN.left - 8}
                y={y(t)}
                textAnchor='end'
                dominantBaseline='middle'
                fontSize={12}
                fill={TEXT_SECONDARY}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {t}%
              </text>
            </g>
          ))}
          {xTicks.map((t) => (
            <text
              key={t}
              x={x(t)}
              y={height - 8}
              textAnchor='middle'
              fontSize={12}
              fill={TEXT_SECONDARY}
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatMillio(t)}
            </text>
          ))}
          {showCurrent ? (
            <g>
              <line
                x1={x(currentIncome)}
                x2={x(currentIncome)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotH}
                stroke={TEXT_SECONDARY}
                strokeWidth={1}
              />
              <text
                x={x(currentIncome) + (x(currentIncome) > width - 90 ? -4 : 4)}
                y={MARGIN.top + 10}
                textAnchor={x(currentIncome) > width - 90 ? 'end' : 'start'}
                fontSize={12}
                fill={TEXT_SECONDARY}
              >
                Jelenlegi bevétel
              </text>
            </g>
          ) : null}
          <g clipPath='url(#tax-chart-plot)'>
            {schemes.map((s) => (
              <path
                key={s.key}
                d={linePath(s.key)}
                fill='none'
                stroke={s.color}
                strokeWidth={2}
                strokeLinejoin='round'
                strokeLinecap='round'
              />
            ))}
          </g>
          {hovered ? (
            <g pointerEvents='none'>
              <line
                x1={x(hovered.income)}
                x2={x(hovered.income)}
                y1={MARGIN.top}
                y2={MARGIN.top + plotH}
                stroke={TEXT_SECONDARY}
                strokeWidth={1}
                opacity={0.5}
              />
              {schemes.map((s) => {
                const v = hovered.values[s.key];
                if (v === null || v > yMax) return null;
                return (
                  <circle
                    key={s.key}
                    cx={x(hovered.income)}
                    cy={y(v)}
                    r={4}
                    fill={s.color}
                    stroke={SURFACE}
                    strokeWidth={2}
                  />
                );
              })}
            </g>
          ) : null}
        </svg>
        {hovered ? (
          <Box
            role='status'
            sx={{
              position: 'absolute',
              top: MARGIN.top,
              left: tooltipOnLeft ? 'auto' : tooltipLeft + 12,
              right: tooltipOnLeft ? width - tooltipLeft + 12 : 'auto',
              pointerEvents: 'none',
              backgroundColor: SURFACE,
              border: `1px solid ${GRID}`,
              borderRadius: 1,
              boxShadow: 1,
              padding: '8px 10px',
              minWidth: 150,
            }}
          >
            <Typography variant='caption' sx={{ color: TEXT_SECONDARY }}>
              Bevétel: {formatMillio(hovered.income)} Ft
            </Typography>
            {schemes.map((s) => {
              const v = hovered.values[s.key];
              return (
                <Box
                  key={s.key}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 2,
                      borderRadius: 1,
                      backgroundColor: s.color,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, minWidth: 44 }}
                  >
                    {v === null ? '–' : formatPct(v)}
                  </Typography>
                  <Typography variant='body2' sx={{ color: TEXT_SECONDARY }}>
                    {s.short}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default TaxChart;
